import type {
  DailyPoint,
  Entry,
  EntryInput,
  Mood,
  Summary,
  TodayStatus,
} from "./types.ts";

/** Error de la API, con el codigo que devuelve el backend. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const BASE = "/api/v1";

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const body = payload as { error?: string; code?: string } | null;
    throw new ApiError(
      response.status,
      body?.error ?? `Error ${response.status}`,
      body?.code ?? "UNKNOWN",
    );
  }

  return payload as T;
}

export const api = {
  moods: () => request<Mood[]>("/moods"),

  today: () => request<Entry[]>("/today"),

  todayStatus: () => request<TodayStatus>("/status/today"),

  listEntries: (params: { from?: string; to?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) query.set(k, String(v));
    }
    const suffix = query.toString() ? `?${query}` : "";
    return request<Entry[]>(`/entries${suffix}`);
  },

  createEntry: (input: EntryInput) =>
    request<Entry>("/entries", { method: "POST", body: JSON.stringify(input) }),

  updateEntry: (id: string, input: Partial<EntryInput>) =>
    request<Entry>(`/entries/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  deleteEntry: (id: string) =>
    request<void>(`/entries/${id}`, { method: "DELETE" }),

  dailyStats: (params: { from?: string; to?: string } = {}) => {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) query.set(k, String(v));
    }
    const suffix = query.toString() ? `?${query}` : "";
    return request<DailyPoint[]>(`/stats/daily${suffix}`);
  },

  summary: () => request<Summary>("/stats/summary"),
};
